import Phaser from 'phaser';
import { useClockStore } from '@/state/clockStore';

const TEX_LIGHT = 'fx_player_light';
const LIGHT_RADIUS = 220;

function ensureLightTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX_LIGHT)) return;
  const size = LIGHT_RADIUS * 2;
  // Build a radial-gradient bright disc using a canvas texture.
  const canvas = scene.textures.createCanvas(TEX_LIGHT, size, size);
  if (!canvas) return;
  const ctx = canvas.getContext();
  const grad = ctx.createRadialGradient(
    LIGHT_RADIUS, LIGHT_RADIUS, 0,
    LIGHT_RADIUS, LIGHT_RADIUS, LIGHT_RADIUS,
  );
  // White light, soft center so the player isn't washed out.
  grad.addColorStop(0.00, 'rgba(255, 255, 255, 0.35)');
  grad.addColorStop(0.50, 'rgba(255, 255, 255, 0.20)');
  grad.addColorStop(1.00, 'rgba(255, 255, 255, 0.0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  canvas.refresh();
}

interface TargetLike {
  x: number;
  y: number;
}

export class PlayerLightSystem {
  private light: Phaser.GameObjects.Image;
  private target: TargetLike | null = null;

  constructor(scene: Phaser.Scene) {
    ensureLightTexture(scene);
    this.light = scene.add
      .image(0, 0, TEX_LIGHT)
      .setDepth(9100)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0)
      .setVisible(false);
  }

  setTarget(target: TargetLike | null): void {
    this.target = target;
  }

  /** Night intensity 0..1 based on phase (1 at deep night, 0 during day). */
  private nightIntensity(): number {
    const now = useClockStore.getState().getNow();
    if (!now) return 0;
    const t = now.hour + now.minute / 60;
    if (t >= 7 && t < 18) return 0;
    if (t >= 5 && t < 7) return Math.max(0, 1 - (t - 5) / 2);
    if (t >= 18 && t < 20) return (t - 18) / 2;
    return 1;
  }

  update(_timeMs: number): void {
    const intensity = this.nightIntensity();
    if (!this.target || intensity <= 0.01) {
      this.light.setVisible(false);
      this.light.setAlpha(0);
      return;
    }
    this.light.setVisible(true);
    this.light.setPosition(this.target.x, this.target.y);
    this.light.setAlpha(intensity);
    this.light.setScale(1);
  }

  destroy(): void {
    this.light.destroy();
  }
}
